import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { WalletGauge } from '../components/dashboard/WalletGauge';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';
import { BAR_GRADIENTS } from '../components/dashboard/theme';

const API = process.env.REACT_APP_BACKEND_URL || '';

/* ── Skeleton primitives ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

/* ── Single customer-card skeleton — used both for the initial page skeleton and the
   infinite-scroll "loading more rows" placeholder ── */
const CustomerCardSkeleton = ({ delay = 0 }) => (
  <div className="rounded-2xl p-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
    <SB className="h-2 w-16 mb-1.5" delay={delay} />
    <SB className="h-4 w-[70%] mb-3" delay={delay + 20} />
    <div className="flex items-center gap-3.5">
      <div className="skel-bone skel-round shrink-0" style={{ width: 76, height: 76, animationDelay: `${delay + 40}ms` }} />
      <div className="flex-1 space-y-2">
        <SB className="h-2.5 w-[80%]" delay={delay + 60} />
        <SB className="h-2 w-[55%]" delay={delay + 80} />
        <SB className="h-2.5 w-[65%]" delay={delay + 100} />
      </div>
    </div>
    <SB className="h-5 w-36 mt-3 !rounded-full" delay={delay + 120} />
  </div>
);

/* ── CI-specific skeleton: 6 KPI cards + 3 rows of customer cards ── */
const CISkeleton = () => (
  <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="ci-skeleton">
    <div className="max-w-[1240px] mx-auto">
      {/* Header */}
      <SB className="h-2 w-56 mb-2" delay={0} />
      <SB className="h-6 w-[480px] mb-1.5" delay={30} />
      <SB className="h-3 w-[520px] mb-4" delay={60} />

      {/* 6 KPI cards */}
      <div className="grid grid-cols-6 gap-3 mb-5">
        {[0,1,2,3,4,5].map(i => (
          <div key={i} className="rounded-[14px] p-3.5 relative overflow-hidden" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
            <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: 'var(--skel-base)' }} />
            <SB className="h-2 w-16 mb-2.5" delay={80 + i * 30} />
            <SB className="h-5 w-20 mb-2" delay={100 + i * 30} />
            <SB className="h-2 w-14" delay={120 + i * 30} />
          </div>
        ))}
      </div>

      {/* Section heading */}
      <div className="flex items-center gap-2.5 mb-3">
        <SB className="h-3 w-64" delay={260} />
        <div className="flex-1 h-px" style={{ background: 'var(--skel-border)' }} />
      </div>

      {/* 3 rows × 3 customer cards */}
      {[0, 1, 2].map(row => (
        <div key={row} className="grid grid-cols-3 gap-3.5 mb-3.5">
          {[0, 1, 2].map(col => (
            <CustomerCardSkeleton key={col} delay={(row * 3 + col) * 40 + 280} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

const fmtM = (v) => {
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3) return '$' + (v / 1e3).toFixed(0) + 'k';
  return '$' + v.toFixed(0);
};
const fmtT = (v) => typeof v === 'number' ? v.toLocaleString() + ' t' : v + ' t';
const disp = (v, m) => m === 'val' ? fmtM(v) : fmtT(v);

const GUIDE = {
  sections: [
    { title: 'Customer Cards', desc: 'Every card is a buyer Sharat sells to, sorted by wallet share (lowest first). The gauge shows what percentage of that buyer\'s total Indian imports comes from Sharat.', example: 'Example: LLC Unifrost (Russia) — Sharat holds 14% wallet share ($12.15M of their $86.8M total). That means 86% goes to competitors.' },
    { title: 'Wallet Share Gauge', desc: 'Two bases: by value (FOB $) and by volume (tonnes). Toggle at the top. A gap between value and volume share means Sharat prices above or below market.', example: 'Example: If value share is 14% but volume share is 17%, Sharat is pricing slightly below the market average for that buyer.' },
    { title: 'Who Else Section', desc: 'Click any customer card to see every Indian exporter shipping to that buyer, ranked by value. Each supplier shows their product form tags.', example: 'Example: NAS Fisheries ships HLSO, PDTO, PD/P&D to Unifrost — forms Sharat doesn\'t offer to this buyer = cross-sell gap.' },
    { title: 'Product Gap Panel', desc: '"WE SUPPLY" vs "THEY ALSO BUY" shows what Sharat sells vs what the buyer buys from all suppliers. Forms marked with a star are bought from rivals only.', example: 'Example: If Unifrost buys PUD from rivals but not Sharat, that\'s revenue Sharat can capture by adding PUD to their offer.' },
  ],
  lookFor: 'Low wallet share + many competitors = growth opportunity. High wallet share with a single competitor = defend aggressively. Starred product forms in the gap panel = immediate cross-sell targets.',
};

/* ── Badge component ── */
const Badge = ({ customer, metric }) => {
  const w = metric === 'val' ? customer.wallet_val : customer.wallet_qty;
  if (w >= 99) return <span className="inline-block font-mono text-[10px] px-2 py-0.5 rounded-full mt-3 tracking-wide bg-[rgba(143,214,148,0.14)] text-[#8FD694] border border-[rgba(143,214,148,0.3)]">SOLE SUPPLIER &middot; PROTECT</span>;
  if (customer.n_competitors >= 8) return <span className="inline-block font-mono text-[10px] px-2 py-0.5 rounded-full mt-3 tracking-wide bg-[rgba(255,107,82,0.14)] text-dash-coral border border-[rgba(255,107,82,0.32)]">{customer.n_competitors} COMPETITORS &middot; CONTESTED</span>;
  return <span className="inline-block font-mono text-[10px] px-2 py-0.5 rounded-full mt-3 tracking-wide bg-[rgba(242,177,76,0.14)] text-dash-amber border border-[rgba(242,177,76,0.3)]">{customer.n_competitors} COMPETITORS</span>;
};

/* ── Supplier Row ── */
const SupplierRow = ({ sup, maxVal, metric, sharatFormSet }) => {
  const v = metric === 'val' ? sup.val : sup.qty;
  const w = maxVal > 0 ? (v / maxVal * 100) : 0;

  return (
    <div className={`grid grid-cols-[1fr_auto] gap-x-2.5 items-center p-2.5 rounded-[10px] border ${sup.is_sharat ? 'bg-[rgba(255,107,82,0.1)] border-[#C9543F]' : 'bg-white/[0.02] border-dash-line'}`}>
      <span className={`text-[13px] truncate ${sup.is_sharat ? 'text-dash-coral font-semibold' : 'text-dash-ink'}`}>
        {sup.is_sharat ? '★ ' : ''}{sup.name}
      </span>
      <span className="font-mono text-xs text-dash-mut text-right">
        {disp(v, metric)}{metric === 'val' ? ` · ${sup.qty}t` : ''}
      </span>
      <div className="col-span-2 h-[7px] bg-dash-panel2 rounded-[5px] overflow-hidden mt-0.5">
        <div
          className={`h-full rounded-[5px] bg-gradient-to-r ${sup.is_sharat ? BAR_GRADIENTS.coral : BAR_GRADIENTS.ice}`}
          style={{ width: `${w.toFixed(0)}%`, transition: 'width 0.4s ease' }}
        />
      </div>
      {sup.forms && sup.forms.length > 0 && (
        <div className="col-span-2 flex flex-wrap gap-1.5 mt-1">
          {sup.forms.map(f => (
            <span
              key={f}
              className={`font-mono text-[9.5px] px-1.5 py-0.5 rounded-[5px] border ${
                sharatFormSet.has(f)
                  ? 'text-dash-ice border-[rgba(79,195,201,0.4)] bg-dash-panel2'
                  : 'text-dash-amber border-[rgba(242,177,76,0.45)] bg-[rgba(242,177,76,0.08)]'
              }`}
            >
              {f}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main Page ── */
export default function CompetitiveIntelligenceReact() {
  const [data, setData] = useState(null);
  const [origData, setOrigData] = useState(null);
  const [months, setMonths] = useState([]);
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState('val');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const CUSTOMERS_PAGE_SIZE = 9; // 3 cards/row × 3 rows
  const [visibleCount, setVisibleCount] = useState(CUSTOMERS_PAGE_SIZE);
  const [customerScrollH, setCustomerScrollH] = useState(null);
  const [customerLoadingMore, setCustomerLoadingMore] = useState(false);
  const customerScrollRef = useRef(null);
  const customerGridRef = useRef(null);
  const customerSentinelRef = useRef(null);
  const customerLoadTimeoutRef = useRef(null);
  const customerIsLoadingRef = useRef(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/static/competitive_intelligence_full.json`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
      fetch(`${API}/api/dashboard-data/months`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
    ]).then(([main, mon]) => {
      setData(main);
      setOrigData(main);
      setMonths(mon || []);
      setToIdx((mon?.length || 1) - 1);
      setLoading(false);
    }).catch(e => {
      setError(e.message);
      setLoading(false);
    });
  }, []);

  const applyPeriod = useCallback(() => {
    if (fromIdx > toIdx) { alert('From must be before To'); return; }
    setPeriodLoading(true);
    const selMonths = months.slice(fromIdx, toIdx + 1).join(',');
    fetch(`${API}/api/dashboard-data/competitive?months=${encodeURIComponent(selMonths)}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        if (d.error) { alert(d.error); setPeriodLoading(false); return; }
        setData(d);
        setSelectedIdx(0);
        setIsFiltered(true);
        setPeriodLoading(false);
      })
      .catch(e => { alert('Failed to apply period: ' + e.message); setPeriodLoading(false); });
  }, [months, fromIdx, toIdx]);

  const resetPeriod = useCallback(() => {
    if (!origData) return;
    setData(origData);
    setSelectedIdx(0);
    setIsFiltered(false);
  }, [origData]);

  const wallet = (c) => metric === 'val' ? c.wallet_val : c.wallet_qty;
  const altWallet = (c) => metric === 'val' ? c.wallet_qty : c.wallet_val;

  const sortedCustomers = useMemo(() => {
    if (!data?.customers) return [];
    return data.customers
      .map((c, i) => ({ ...c, _origIdx: i }))
      .sort((a, b) => wallet(a) - wallet(b));
  }, [data, metric]);

  // Re-collapse to the first page whenever the underlying/sorted list changes (new period, metric toggle).
  useEffect(() => {
    setVisibleCount(CUSTOMERS_PAGE_SIZE);
    clearTimeout(customerLoadTimeoutRef.current);
    customerIsLoadingRef.current = false;
    setCustomerLoadingMore(false);
  }, [data, metric]);

  const visibleCustomers = sortedCustomers.slice(0, visibleCount);

  // Lock in the scroll panel's height from the real rendered height of the first 3 rows,
  // so the panel itself scrolls internally instead of the whole page growing.
  useEffect(() => {
    if (customerScrollH != null) return;
    const grid = customerGridRef.current;
    if (!grid || grid.children.length === 0) return;
    const rowsToShow = Math.min(CUSTOMERS_PAGE_SIZE, sortedCustomers.length);
    const lastCard = grid.children[rowsToShow - 1];
    if (!lastCard) return;
    const gridTop = grid.getBoundingClientRect().top;
    const lastBottom = lastCard.getBoundingClientRect().bottom;
    setCustomerScrollH(Math.ceil(lastBottom - gridTop));
  }, [visibleCustomers, sortedCustomers.length, customerScrollH]);

  // Infinite scroll within the panel: grow visibleCount as the sentinel scrolls into view
  // inside the bounded customer-scroll container (not the page/window). Data is already fully
  // in memory, so a short artificial delay + skeleton cards make the "loading" feel real.
  useEffect(() => {
    if (customerScrollH == null) return undefined;
    if (visibleCount >= sortedCustomers.length) return undefined;
    const el = customerSentinelRef.current;
    const root = customerScrollRef.current;
    if (!el || !root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !customerIsLoadingRef.current) {
        customerIsLoadingRef.current = true;
        setCustomerLoadingMore(true);
        customerLoadTimeoutRef.current = setTimeout(() => {
          setVisibleCount((v) => Math.min(v + CUSTOMERS_PAGE_SIZE, sortedCustomers.length));
          setCustomerLoadingMore(false);
          customerIsLoadingRef.current = false;
        }, 500);
      }
    }, { root, rootMargin: '150px' });
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(customerLoadTimeoutRef.current);
    };
  }, [visibleCount, sortedCustomers.length, customerScrollH]);

  const selectedCustomer = data?.customers?.[selectedIdx] || null;
  const sharatFormSet = useMemo(() => new Set((selectedCustomer?.sharat_forms || []).map(f => f.name)), [selectedCustomer]);
  const sortedSuppliers = useMemo(() => {
    if (!selectedCustomer?.suppliers) return [];
    return [...selectedCustomer.suppliers].sort((a, b) => (metric === 'val' ? b.val - a.val : b.qty - a.qty));
  }, [selectedCustomer, metric]);
  const maxSupVal = useMemo(() => Math.max(...sortedSuppliers.map(s => metric === 'val' ? s.val : s.qty), 1), [sortedSuppliers, metric]);

  if (loading) return <CISkeleton />;
  if (error) return <div className="h-full bg-dash-bg flex items-center justify-center"><div className="text-dash-coral font-mono text-sm">Error: {error}</div></div>;

  const kpi = data.kpi;
  const kpiCards = [
    { label: 'Our Exports', value: metric === 'val' ? fmtM(kpi.val) : fmtT(kpi.qty), sub: metric === 'val' ? 'FOB value' : 'tonnes', accent: 'var(--dash-coral)' },
    { label: 'Volume', value: kpi.qty + ' t', sub: 'tonnes', accent: 'var(--dash-ice)' },
    { label: 'Shipments', value: kpi.ship.toLocaleString(), sub: 'consignments', accent: 'var(--dash-amber)' },
    { label: 'Customers', value: kpi.customers, sub: 'active buyers', accent: '#8FD694' },
    { label: 'Countries', value: kpi.countries, sub: 'destinations', accent: '#B98CD6' },
    { label: 'Our Product', value: data.sharat_forms.map(f => f.name).slice(0, 3).join(', '), sub: data.sharat_forms.length > 3 ? `+${data.sharat_forms.length - 3} more` : 'forms', accent: '#F08FA8' },
  ];

  const gapSet = new Set(selectedCustomer?.gap_forms || []);
  const periodLabel = isFiltered
    ? `${months[fromIdx]} to ${months[toIdx]} (${toIdx - fromIdx + 1} month${toIdx - fromIdx === 0 ? '' : 's'})`
    : `All ${months.length || 23} months · ${months[0]} to ${months[months.length - 1]}`;

  return (
    <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="competitive-intelligence-react">
      <div className="max-w-[1240px] mx-auto">

        {/* ── Header ── */}
        <div className="flex flex-wrap gap-4 items-end justify-between mb-4">
          <div>
            <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-dash-coral mb-2">
              Competitor & Customer Intelligence &middot; Apr 2024 – Feb 2026
            </div>
            <h1 className="text-[28px] font-grotesk font-extrabold tracking-tight">
              <span className="text-dash-coral">{data.shipper}</span> — who buys from us, who else they buy from
            </h1>
            <p className="text-dash-mut text-[13px] mt-1.5 max-w-[760px]">
              &ldquo;Wallet share&rdquo; = Sharat&rsquo;s share of each customer&rsquo;s total Indian imports. Toggle the basis between FOB value and volume — both are shown on every gauge.
            </p>
          </div>
          <div className="flex bg-dash-panel border border-dash-line rounded-[10px] p-[3px] gap-0.5" data-testid="metric-toggle">
            <button
              onClick={() => setMetric('val')}
              className={`font-mono text-xs px-4 py-2 rounded-[7px] transition ${metric === 'val' ? 'bg-dash-coral text-[#240b06] font-bold' : 'text-dash-mut hover:text-dash-ink'}`}
              data-testid="toggle-val"
            >
              By Value&nbsp;$
            </button>
            <button
              onClick={() => setMetric('qty')}
              className={`font-mono text-xs px-4 py-2 rounded-[7px] transition ${metric === 'qty' ? 'bg-dash-coral text-[#240b06] font-bold' : 'text-dash-mut hover:text-dash-ink'}`}
              data-testid="toggle-qty"
            >
              By Volume&nbsp;t
            </button>
          </div>
        </div>

        {/* Period Filter */}
        {months.length > 0 && (
          <div className="mb-4">
            <PeriodFilter
              months={months}
              fromIdx={fromIdx}
              toIdx={toIdx}
              onFromChange={setFromIdx}
              onToChange={setToIdx}
              onApply={applyPeriod}
              onReset={resetPeriod}
              periodLabel={periodLabel}
              isFiltered={isFiltered}
              loading={periodLoading}
            />
          </div>
        )}

        {/* ── KPI Cards (6 col) ── */}
        <div className="grid grid-cols-6 gap-3 mb-5" data-testid="kpi-cards-ci">
          {kpiCards.map((k, i) => (
            <div key={i} className="bg-gradient-to-br from-dash-panel to-dash-panel2 border border-dash-line rounded-[14px] p-3.5 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: k.accent }} />
              <div className="text-[10px] tracking-[0.13em] uppercase text-dash-mut mb-2">{k.label}</div>
              <div className="font-grotesk font-bold text-[22px] leading-none text-dash-ink truncate">{k.value}</div>
              <div className="font-mono text-[10.5px] text-[#5C7980] mt-1.5">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Section: Customer Cards ── */}
        <h2 className="text-[13px] font-bold tracking-[0.04em] uppercase text-dash-ice flex items-center gap-2.5 mb-3" data-testid="section-customers">
          Sharat&rsquo;s existing customers &amp; wallet share
          <span className="flex-1 h-px bg-dash-line" />
        </h2>

        <div className="mb-6">
          <div
            ref={customerScrollRef}
            className="overflow-y-auto dash-scrollbar pr-3"
            style={customerScrollH != null ? { maxHeight: `${customerScrollH}px` } : undefined}
            data-testid="customer-scroll-container"
          >
            <div ref={customerGridRef} className="grid grid-cols-3 gap-3.5" data-testid="customer-grid">
              {visibleCustomers.map((c) => (
                <div
                  key={c._origIdx}
                  onClick={() => setSelectedIdx(c._origIdx)}
                  className={`bg-dash-panel border rounded-2xl p-4 cursor-pointer transition-all hover:border-dash-ice ${
                    selectedIdx === c._origIdx ? 'border-dash-coral shadow-[0_0_0_1px_var(--dash-coral)]' : 'border-dash-line'
                  }`}
                  data-testid={`customer-card-${c._origIdx}`}
                >
                  <div className="font-mono text-[10px] tracking-[0.1em] uppercase text-dash-mut">{c.country}</div>
                  <div className="font-grotesk font-bold text-[17px] leading-tight mt-0.5 mb-3">{c.name}</div>
                  <div className="flex items-center gap-3.5">
                    <WalletGauge pct={wallet(c)} altPct={altWallet(c)} metric={metric} />
                    <div className="flex-1 text-xs text-dash-mut leading-relaxed">
                      We sell: <b className="text-dash-ink font-mono">{fmtM(c.sharat_val)}</b><br />
                      {c.sharat_qty} t &middot; {c.sharat_ship} shp<br />
                      Their total: <b className="text-dash-ink font-mono">{disp(metric === 'val' ? c.total_val : c.total_qty, metric)}</b>
                    </div>
                  </div>
                  <Badge customer={c} metric={metric} />
                </div>
              ))}

              {customerLoadingMore && Array.from({ length: Math.min(CUSTOMERS_PAGE_SIZE, sortedCustomers.length - visibleCount) }).map((_, i) => (
                <CustomerCardSkeleton key={`skeleton-${i}`} delay={i * 40} />
              ))}
            </div>

            {visibleCount < sortedCustomers.length && (
              <div ref={customerSentinelRef} className="h-px" data-testid="customer-grid-sentinel" />
            )}
          </div>
        </div>

        {/* ── Section: Who Else ── */}
        {selectedCustomer && (
          <>
            <h2 className="text-[13px] font-bold tracking-[0.04em] uppercase text-dash-ice flex items-center gap-2.5 mb-3" data-testid="section-who-else">
              Who else is supplying <span className="text-dash-coral">{selectedCustomer.name}</span>?
              <span className="flex-1 h-px bg-dash-line" />
            </h2>

            <div className="grid grid-cols-[1.4fr_1fr] gap-3.5" data-testid="who-else-panel">
              {/* Supplier list */}
              <div className="bg-dash-panel border border-dash-line rounded-2xl p-5">
                <div className="font-grotesk font-bold text-[15px] mb-1">
                  Suppliers of {selectedCustomer.name} — {selectedCustomer.country}
                </div>
                <div className="text-dash-mut text-xs mb-3.5">
                  {selectedCustomer.n_suppliers} Indian exporter{selectedCustomer.n_suppliers > 1 ? 's' : ''} ship to this buyer &middot; ranked by {metric === 'val' ? 'value' : 'volume'}. Sharat highlighted.
                </div>
                <div className="flex flex-col gap-2.5 max-h-[500px] overflow-y-auto">
                  {sortedSuppliers.map((s, i) => (
                    <SupplierRow key={i} sup={s} maxVal={maxSupVal} metric={metric} sharatFormSet={sharatFormSet} />
                  ))}
                </div>
              </div>

              {/* Product gap */}
              <div className="bg-dash-panel border border-dash-line rounded-2xl p-5">
                <div className="font-grotesk font-bold text-[15px] mb-1">Same as we supply, or different?</div>
                <div className="text-dash-mut text-xs mb-3.5">
                  Sharat sells {selectedCustomer.sharat_forms?.length || 0} form{(selectedCustomer.sharat_forms?.length || 0) !== 1 ? 's' : ''}. Here&rsquo;s what this customer also sources elsewhere.
                </div>

                <div className="flex items-center gap-2.5 mb-3">
                  <span className="font-mono text-[11px] uppercase tracking-[0.05em] text-dash-mut w-24 shrink-0">We supply</span>
                  <div className="flex flex-wrap gap-1.5">
                    {(selectedCustomer.sharat_forms || []).map(f => (
                      <span key={f.name} className="font-mono text-[11px] px-2.5 py-1 rounded-[7px] bg-[rgba(255,107,82,0.14)] text-dash-coral border border-[#C9543F]">{f.name}</span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2.5 mb-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.05em] text-dash-mut w-24 shrink-0">They also buy</span>
                  <div className="flex flex-wrap gap-1.5">
                    {(selectedCustomer.other_forms?.length ? selectedCustomer.other_forms : [{ name: '—' }]).map(f => (
                      <span
                        key={f.name}
                        className={`font-mono text-[11px] px-2.5 py-1 rounded-[7px] border ${
                          gapSet.has(f.name)
                            ? 'bg-[rgba(242,177,76,0.12)] text-dash-amber border-[rgba(242,177,76,0.4)]'
                            : 'bg-[rgba(79,195,201,0.12)] text-dash-ice border-[rgba(79,195,201,0.35)]'
                        }`}
                      >
                        {f.name}{gapSet.has(f.name) ? ' ✦' : ''}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Verdict */}
                {wallet(selectedCustomer) >= 99 ? (
                  <div className="p-3.5 rounded-[11px] text-[13px] leading-relaxed bg-[rgba(143,214,148,0.08)] border border-[rgba(143,214,148,0.3)] text-[#BfE6C2]">
                    <b className="text-dash-ink">{selectedCustomer.name} buys only from Sharat.</b> Same product, no competitor — a locked relationship to protect and grow.
                  </div>
                ) : selectedCustomer.gap_forms?.length > 0 ? (
                  <div className="p-3.5 rounded-[11px] text-[13px] leading-relaxed bg-[rgba(242,177,76,0.08)] border border-[rgba(242,177,76,0.3)] text-[#F4D9A6]" data-testid="verdict-gap">
                    <b className="text-dash-ink">Product gap.</b> We supply only {selectedCustomer.sharat_forms.map(f => f.name).join(', ')}, but {selectedCustomer.name} also buys <b className="text-dash-ink">{selectedCustomer.gap_forms.join(', ')}</b> from rivals (✦). That spend is leaving the table — a clear cross-sell opening.
                  </div>
                ) : (
                  <div className="p-3.5 rounded-[11px] text-[13px] leading-relaxed bg-[rgba(143,214,148,0.08)] border border-[rgba(143,214,148,0.3)] text-[#BfE6C2]">
                    Competitors largely sell the <b className="text-dash-ink">same form</b> we do — here we compete on price, reliability and size range.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── Footer ── */}
        <div className="mt-6 border-t border-dash-line pt-4 text-[#5C7980] text-[11.5px] leading-relaxed">
          <b>Wallet share</b> is shown on two bases: <b>by value</b> = Sharat FOB$ ÷ buyer&rsquo;s total Indian import FOB$; <b>by volume</b> = Sharat tonnes ÷ buyer&rsquo;s total import tonnes. Apr 2024 – Feb 2026 shipment data; product forms parsed from descriptions; Unifrost name variants merged. Single-month snapshot.
        </div>

        <FloatingFAB guideContent={GUIDE} />
      </div>
    </div>
  );
}
